import { useCallback, useRef, useState } from 'react';
import type {
  HITLDecision,
  HITLRequestConfirmationDecision,
  HITLRequestConfirmationMessage,
  PromptOption,
} from '@terra/shared-dto-chat-types';
import type { OnCloseEditInMainContent } from '../../hitl-types';
import type { HITLActionMessageEvent } from '../hitl-decision.utils';

type ResolvedHITLRequestConfirmationDecision =
  NonNullable<HITLRequestConfirmationDecision>;

export type HitlDecisionDispatchArgs = {
  decision: ResolvedHITLRequestConfirmationDecision;
  option: PromptOption;
  decisions?: HITLDecision[];
};

export type OnHITLActionMessageEvent = (
  event: HITLActionMessageEvent,
) => void | Promise<void>;

export function useHitlDecisionDispatch({
  dataMessage,
  messagePartId,
  isActionable,
  onHITLActionMessageEvent,
  onCloseEditInMainContent,
}: {
  dataMessage?: HITLRequestConfirmationMessage;
  messagePartId?: string;
  isActionable: boolean;
  onHITLActionMessageEvent?: OnHITLActionMessageEvent;
  onCloseEditInMainContent?: OnCloseEditInMainContent;
}) {
  const [pendingDecision, setPendingDecision] =
    useState<ResolvedHITLRequestConfirmationDecision | null>(null);
  const isPendingRef = useRef(false);

  const clearPendingDecision = useCallback(() => {
    isPendingRef.current = false;
    setPendingDecision(null);
  }, []);

  const dispatchDecision = useCallback(
    ({ decision, option, decisions }: HitlDecisionDispatchArgs): void => {
      if (isPendingRef.current || !dataMessage || !isActionable) return;
      isPendingRef.current = true;
      setPendingDecision(decision);
      const resolvedDataMessage =
        dataMessage.id || !messagePartId
          ? dataMessage
          : { ...dataMessage, id: messagePartId };
      const submission = onHITLActionMessageEvent?.({
        dataMessage: resolvedDataMessage,
        selectedOption: option,
        decision,
        decisions,
      });

      if (submission instanceof Promise) {
        submission.catch(clearPendingDecision);
      } else {
        clearPendingDecision();
      }

      if (decision.type !== 'edit') {
        const hitlMessageId = messagePartId ?? dataMessage.id;

        if (hitlMessageId) {
          onCloseEditInMainContent?.(hitlMessageId);
        }
      }
    },
    [
      clearPendingDecision,
      dataMessage,
      isActionable,
      onHITLActionMessageEvent,
      messagePartId,
      onCloseEditInMainContent,
    ],
  );

  return { dispatchDecision, pendingDecision, isPendingRef };
}
